import { AlertType, ApiResult } from "src/api/types";
import { usePageConfigProviderContext } from "src/features/PageConfigProvider";
import { useTranslation } from "src/hooks";
import BigNumber from "bignumber.js";
import {
  amountDivideDecimals,
  displayAmount,
  findDecimalByChainNameAndAsset,
  listMappingTransform,
  toDisplayTime,
} from "src/utils/helper";

import { TableColumnKey, TableRow } from "../types/Table";
import { useChains } from "src/components/FetchConfig";
import { GetListRes, LegerBalanceMonitorDetail } from "../types/GetListRes";

import EnumCriteria from "@wallet-manager/node-types/dist/src/postgres/const/Criteria";

type MapResultToTupleArray = (
  row: GetListRes[],
  omitKeys: string[],
) => any[][][];

const useTransformRowData = ({
  apiList,
}: {
  apiList: ApiResult<GetListRes>;
}) => {
  const { translatePrefix } = usePageConfigProviderContext();
  const { t, te } = useTranslation(translatePrefix);
  const { selectChainByPieces } = useChains();

  const mapResultToTupleArray: MapResultToTupleArray = (rows, omitKeys) => {
    return rows.map((item) => {
      const getChainCode = () => {
        const chainInfo = selectChainByPieces({
          chain_type: item.chain_type,
          chain_id: item.chain_id,
        });

        return chainInfo?.chain_code || "";
      };

      const getChainName = () => {
        const chainInfo = selectChainByPieces({
          chain_type: item.chain_type,
          chain_id: item.chain_id,
        });

        return chainInfo?.name || "";
      };

      const getAlertType = () => {
        if (!item.details?.length) return AlertType.FixedThreshold;
        const isFixedThreshold = item.details.some(
          (detail) =>
            detail.criteria === EnumCriteria.LessThan ||
            detail.criteria === EnumCriteria.GreaterThan,
        );
        return isFixedThreshold
          ? AlertType.FixedThreshold
          : AlertType.DynamicThreshold;
      };

      const getAlertTypeName = () => {
        const alertType = getAlertType();
        return te(AlertType[alertType] as any);
      };

      const getThresholdValue = (
        detail: LegerBalanceMonitorDetail | undefined,
      ) => {
        if (!detail) {
          return undefined;
        }

        const decimal = findDecimalByChainNameAndAsset(
          String(detail.chain_type),
          detail.chain_id,
          detail.asset_name,
        );

        return amountDivideDecimals(detail.threshold_value, Number(decimal));
      };

      const getThreshold = () => {
        let lowerThreshold: string | undefined = undefined;
        let upperThreshold: string | undefined = undefined;

        if (!item.details) {
          return { lowerThreshold, upperThreshold };
        }

        const lowerThresholdDetail = item.details.find(
          (detail) =>
            detail.criteria === EnumCriteria.LessThan ||
            detail.criteria === EnumCriteria.SlidingLessThan,
        );
        const upperThresholdDetail = item.details.find(
          (detail) =>
            detail.criteria === EnumCriteria.GreaterThan ||
            detail.criteria === EnumCriteria.SlidingGreaterThan,
        );

        lowerThreshold = getThresholdValue(lowerThresholdDetail);
        upperThreshold = getThresholdValue(upperThresholdDetail);

        return { lowerThreshold, upperThreshold };
      };

      const getDynamicConfig = () => {
        let stepValue: string | undefined = undefined;
        let balance: string | undefined = undefined;

        if (!item.details) {
          return { stepValue, balance };
        }

        const detail = item.details.find(
          (detail) =>
            detail.criteria === EnumCriteria.SlidingLessThan ||
            detail.criteria === EnumCriteria.SlidingGreaterThan,
        );

        if (detail) {
          const decimal = findDecimalByChainNameAndAsset(
            String(detail.chain_type),
            detail.chain_id,
            detail.asset_name,
          );

          if (detail.step_value) {
            stepValue = amountDivideDecimals(
              detail.step_value,
              Number(decimal),
            );
          }
          if (detail.balance) {
            balance = amountDivideDecimals(detail.balance, Number(decimal));
          }
        }

        return { stepValue, balance };
      };

      const { lowerThreshold, upperThreshold } = getThreshold();
      const { stepValue, balance } = getDynamicConfig();

      const mappedResult = [
        [TableColumnKey.ClientId, t(TableColumnKey.ClientId), item.client_id],
        [TableColumnKey.ChainCode, t(TableColumnKey.ChainCode), getChainCode()],
        [TableColumnKey.ChainName, t(TableColumnKey.ChainName), getChainName()],
        [TableColumnKey.Currency, t(TableColumnKey.Currency), item.asset_name],
        [TableColumnKey.AlertType, t(TableColumnKey.AlertType), getAlertType()],
        [
          TableColumnKey.AlertTypeName,
          t(TableColumnKey.AlertType),
          getAlertTypeName(),
        ],
        [TableColumnKey.StepValue, t(TableColumnKey.StepValue), stepValue],
        [TableColumnKey.Balance, t(TableColumnKey.Balance), balance],
        [
          TableColumnKey.ThresholdLower,
          t(TableColumnKey.ThresholdLower),
          lowerThreshold,
        ],
        [
          TableColumnKey.ThresholdUpper,
          t(TableColumnKey.ThresholdUpper),
          upperThreshold,
        ],
        [
          TableColumnKey.CreationTime,
          t(TableColumnKey.CreationTime),
          toDisplayTime(item?.created_date),
        ],
        [
          TableColumnKey.LastModifiedTime,
          t(TableColumnKey.LastModifiedTime),
          toDisplayTime(item?.last_modified_date),
        ],
        ["rawData", "", item],
      ].filter(([key]) => !omitKeys.includes(key as string));
      return mappedResult;
    });
  };

  const res = mapResultToTupleArray(apiList.rows, []);
  const rows: TableRow[] = res.map(listMappingTransform("key"));

  return {
    rows,
    totalCounts: apiList.count,
    mapResultToTupleArray,
  };
};

export default useTransformRowData;
